import React, { useState, useEffect, useCallback, cloneElement } from 'react';
import { Carousel } from 'components/Carousel';
import { observer } from 'mobx-react-lite';
import { cx } from '@linaria/core';
import { Input } from 'api/grpc/resources';
import { carousel, wrapper } from 'components/InputActionBar/InputActionBar.styles';
import { InputNavigation } from 'components/InputActionBar/InputNavigation';
import { InfoButton } from 'modules/InputViewer/components/InputInfo/InfoButton';

interface Props {
  className?: string;
  inputs: CF.API.Inputs.Input[];
  currentInputId: string;
  altPrefix?: string;
  setCurrentInput: (ipt: CF.API.Inputs.Input) => void;
  topActions?: { key: string; element: JSX.Element }[];
  bottomActions?: { key: string; element: JSX.Element }[];
  currentInput?: Input | null;
}

const InputActionBar_ = ({
  inputs,
  setCurrentInput,
  currentInputId,
  altPrefix,

  topActions = [],
  bottomActions = [],
  className,
  currentInput,
}: Props): JSX.Element => {
  const [currentIndex, setCurrentInputFromIndex] = useSyncedInputState(currentInputId, inputs, setCurrentInput);
  // const store = useInputsManagerStore(); + store.currentInput

  // InputNavigation deals with UI position; i.e. natural numbers. We convert it to index here.
  const navHandler = useCallback(
    (nthPosition: number): void => {
      setCurrentInputFromIndex(nthPosition - 1);
    },
    [setCurrentInputFromIndex],
  );

  return (
    <div className={cx(wrapper, className)} data-testid="inputs-action-bar">
      {topActions.map((item) => cloneElement(item.element, { key: item.key }))}
      {inputs && Boolean(inputs.length) && (
        <Carousel
          inputs={inputs}
          className={carousel}
          altPrefix={altPrefix}
          selected={inputs[currentIndex]?.id || inputs[0].id}
          onSelect={setCurrentInputFromIndex}
        />
      )}
      {Boolean(inputs.length) && <InputNavigation total={inputs.length} current={currentIndex + 1} setCurrent={navHandler} />}
      {Boolean(inputs.length) && currentInput && <InfoButton currentInput={currentInput} />}
      {bottomActions.map((item) => cloneElement(item.element, { key: item.key }))}
    </div>
  );
};

export const InputActionBar = observer(InputActionBar_);

function useSyncedInputState(
  currentInputId: string,
  inputs: CF.API.Inputs.Input[],
  setCurrentInput: (i: CF.API.Inputs.Input) => void,
): [number, (idx: number) => void] {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  useEffect(() => {
    // if store's currentInputId changes due to external factors; like new image upload
    // reflect that change in the carousel's UI
    const foundInputIdx = inputs.findIndex((i) => i.id === currentInputId);
    if (foundInputIdx > -1) setCurrentIndex(foundInputIdx);
  }, [currentInputId, [...inputs]]);

  useEffect(() => {
    // This is to initially set the current input when the component is mounted
    if (inputs.length && !currentInputId) {
      setCurrentInput(inputs[currentIndex]);
    }
  }, [inputs]);

  const setCurrentInputAndIndex = useCallback(
    (idx: number): void => {
      if (idx >= 0 && inputs[idx]) {
        setCurrentIndex(idx);
        setCurrentInput(inputs[idx]);
      }
    },
    [setCurrentIndex, setCurrentInput, inputs],
  );

  return [currentIndex, setCurrentInputAndIndex];
}

export const InputActionBar_testable = {
  useSyncedInputState,
};
